import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useProspectingAvailability } from './useProspectingAvailability';
import { EmailBodyEditor } from './EmailBodyEditor';
import { useAuth } from '../auth/AuthProvider';
import { copySetPhotoErrorMessage, uploadCopySetPhoto } from './copySetPhotos';
import { marksToText } from '../gmail/mailSend';
import {
  Badge,
  Button,
  Checkbox,
  Drawer,
  EmptyState,
  InlineError,
  Input,
  SaveHint,
  Select,
  Skeleton,
  useDebouncedAutoSave,
} from '../../components/ui';
import { useContentItems } from '../contentStudio/hooks';
import type { ContentItem } from '../contentStudio/types';
import {
  useAllMemberships,
  useComposeDraft,
  useCopySets,
  useCreateCopySet,
  useUpdateCopySet,
  useSaveCopyEdit,
} from './hooks';
import { CopyEditConfirmationDialog } from './CopyEditConfirmationDialog';
import {
  copyEditBaseline,
  copyMessageLabel,
  DRAFT_COPY_BADGE,
  DRAFT_COPY_GROUP,
} from './copyMessages';
import {
  beginCopyEdit,
  proposeCopyEdit,
  requestCopyEdit,
  confirmCopyEditFirst,
  confirmCopyEditSecond,
  cancelCopyEditConfirmation,
  startCopyEditSave,
  invalidateCopyEdit,
  reloadCopyEdit,
  retryCopyEditSave,
} from './copyEditConfirmation';
import type { CopyEditState } from './copyEditConfirmation';
import { composeErrorMessage } from './trackerFormat';
import {
  COPY_TOKENS,
  LEGACY_TOUCH_COUNT,
  TOUCHES,
  copyEmail,
  copySetLength,
  copySetPerformance,
  leadName,
  replaceTokens,
  salesErrorMessage,
  topPerformingCopySetId,
} from './types';
import type { CopySet, Lead, Touch } from './types';

const COPY_TYPE_OPTIONS = ['Cold', 'DM', 'Nurture'];

function CopySetEditorDrawer({ set, onClose }: { set: CopySet | null; onClose: () => void }) {
  const { message_enabled } = useProspectingAvailability();
  const versionedSession = useRef(message_enabled);
  if (message_enabled) versionedSession.current = true;
  const workflowPaused = versionedSession.current && !message_enabled;
  const create = useCreateCopySet();
  const update = useSaveCopyEdit();
  const legacyUpdate = useUpdateCopySet();
  const catalog = useCopySets();
  const { client } = useAuth();
  const photoInput = useRef<HTMLInputElement>(null);
  const pendingPhoto = useRef<((file: File | null) => void) | null>(null);
  const [error, setError] = useState('');
  const [savedNotice, setSavedNotice] = useState('');
  const [edit, setEdit] = useState<CopyEditState | null>(() =>
    set?.message_revision && typeof set.message_used === 'boolean'
      ? beginCopyEdit(copyEditBaseline(set))
      : null,
  );
  const [copied, setCopied] = useState<Touch | null>(null);
  const [name, setName] = useState(set?.name ?? '');
  const [type, setType] = useState(set?.type ?? 'Cold');
  const [description, setDescription] = useState(set?.description ?? '');
  const [channel, setChannel] = useState(set?.channel ?? 'email');
  const [emails, setEmails] = useState(() =>
    TOUCHES.map((touch) => {
      const email = set === null ? { subject: '', body: '' } : copyEmail(set, touch);
      return { subject: email.subject, body: email.body };
    }),
  );
  // Drop 139: a set can hold eight emails, but eight empty cards is noise for
  // the four-email sets that are most of the library. Open at the campaign's
  // own length and let Nez add steps one at a time, which is what she asked
  // for. Never below four, so no existing set loses a card it was using.
  const [shownCards, setShownCards] = useState(() =>
    message_enabled && set?.is_one_off
      ? 1
      : Math.max(LEGACY_TOUCH_COUNT, set === null ? 0 : copySetLength(set)),
  );
  const cards = TOUCHES.filter((touch) => touch <= shownCards);

  const pending = create.isPending || update.isPending;
  const editing = set !== null;

  const fieldsOf = useCallback(
    () => ({
      name: name.trim(),
      type,
      channel,
      description,
      e1_subject: emails[0]?.subject ?? '',
      e1_body: emails[0]?.body ?? '',
      e2_subject: emails[1]?.subject ?? '',
      e2_body: emails[1]?.body ?? '',
      e3_subject: emails[2]?.subject ?? '',
      e3_body: emails[2]?.body ?? '',
      e4_subject: emails[3]?.subject ?? '',
      e4_body: emails[3]?.body ?? '',
      e5_subject: emails[4]?.subject ?? '',
      e5_body: emails[4]?.body ?? '',
      e6_subject: emails[5]?.subject ?? '',
      e6_body: emails[5]?.body ?? '',
      e7_subject: emails[6]?.subject ?? '',
      e7_body: emails[6]?.body ?? '',
      e8_subject: emails[7]?.subject ?? '',
      e8_body: emails[7]?.body ?? '',
    }),
    [name, type, channel, description, emails],
  );

  const fieldsRef = useRef(fieldsOf);
  fieldsRef.current = fieldsOf;
  const messageEnabledRef = useRef(message_enabled);
  messageEnabledRef.current = message_enabled;
  // OFF retains the deployed editor's single debounced save for all fields.
  // Never use this as a fallback after a versioned write has failed.
  const setSave = useDebouncedAutoSave<string>(
    useCallback(
      (snapshot: string) => {
        if (set === null || messageEnabledRef.current || versionedSession.current)
          return Promise.resolve();
        const changes = JSON.parse(snapshot) as ReturnType<typeof fieldsOf>;
        if (changes.name === '')
          return Promise.reject(new Error('Give the copy set a name first.'));
        setError('');
        return new Promise((resolve, reject) => {
          legacyUpdate.mutate(
            { copySetId: set.id, changes },
            {
              onSuccess: resolve,
              onError: (cause) => {
                setError(salesErrorMessage(cause.message));
                reject(cause);
              },
            },
          );
        });
      },
      [set, message_enabled, legacyUpdate, fieldsOf],
    ),
  );
  const snapshot = JSON.stringify(fieldsOf());
  const openedWith = useRef(snapshot);
  const saveRef = useRef(setSave.save);
  saveRef.current = setSave.save;
  useEffect(() => {
    if (versionedSession.current || message_enabled || !editing || snapshot === openedWith.current)
      return;
    saveRef.current(snapshot);
  }, [message_enabled, editing, snapshot]);
  useEffect(() => {
    setEdit((previous) => (previous === null ? null : proposeCopyEdit(previous, fieldsOf())));
    setSavedNotice('');
  }, [fieldsOf]);
  // If availability changes while the drawer is open, keep its unsaved fields
  // but require a current versioned baseline before offering the new save flow.
  useEffect(() => {
    if (!message_enabled || edit !== null || set === null) return;
    const current = catalog.data?.find((copy) => copy.id === set.id);
    if (current?.message_revision && typeof current.message_used === 'boolean')
      setEdit(proposeCopyEdit(beginCopyEdit(copyEditBaseline(current)), fieldsOf()));
  }, [message_enabled, edit, set, catalog.data, fieldsOf]);

  function persist(next: CopyEditState, retry = false) {
    if (!message_enabled) return;
    const live = (catalog.data ?? []).find((copy) => copy.id === next.baseline.id);
    const writing = startCopyEditSave(
      next,
      retry ? next.baseline.revision : (live?.message_revision ?? next.baseline.revision),
    );
    setEdit(writing);
    if (writing.phase !== 'saving' || writing.command === null) return;
    setError('');
    update.mutate(writing.command, {
      onSuccess: (copy) => {
        setEdit(
          proposeCopyEdit(
            beginCopyEdit(copyEditBaseline({ ...copy, message_used: writing.baseline.used })),
            fieldsRef.current(),
          ),
        );
        setSavedNotice('Saved. Earlier DMs keep their recorded versions.');
      },
      onError: (cause) => {
        if (
          cause.message.includes('copy_revision_conflict') ||
          cause.message.includes('copy_was_archived') ||
          cause.message.includes('used_copy_needs_confirmed_revision')
        ) {
          setEdit(invalidateCopyEdit(writing));
          setError('This entry changed. Reload it before reviewing and saving again.');
        } else {
          setEdit(retryCopyEditSave(writing));
          setError(salesErrorMessage(cause.message));
        }
      },
    });
  }

  function saveEdit() {
    if (edit === null || name.trim() === '') {
      setError('Reload the copy catalog and give the entry a name before saving.');
      return;
    }
    const next = requestCopyEdit(proposeCopyEdit(edit, fieldsOf()), crypto.randomUUID());
    setEdit(next);
    if (next.phase === 'ready') persist(next);
  }

  async function reloadRevision() {
    const refreshed = await catalog.refetch();
    const current = refreshed.data?.find((copy) => copy.id === set?.id);
    if (edit !== null && current !== undefined) {
      setEdit(reloadCopyEdit(edit, copyEditBaseline(current)));
      setError(
        current.archived_at == null
          ? ''
          : 'This entry is archived. Restore it before changing its wording.',
      );
    }
  }

  function setEmail(index: number, field: 'subject' | 'body', value: string) {
    setEmails((prev) => prev.map((e, i) => (i === index ? { ...e, [field]: value } : e)));
  }

  /** Drops a token into this email's body at the caret (end when untouched). */

  // A photo for a set goes to Drive and is written into the body as
  // drive:<id>, so the next person to send this set has the same picture.
  function pickPhoto(): Promise<{ ref: string; caption: string } | null> {
    return new Promise((resolve) => {
      pendingPhoto.current = (file) => {
        pendingPhoto.current = null;
        if (file === null) {
          resolve(null);
          return;
        }
        uploadCopySetPhoto(client, file).then(resolve, (cause: unknown) => {
          setError(copySetPhotoErrorMessage(cause instanceof Error ? cause.message : ''));
          resolve(null);
        });
      };
      const el = photoInput.current;
      if (el === null) {
        pendingPhoto.current(null);
        return;
      }
      el.value = '';
      el.click();
    });
  }

  function copyToClipboard(index: number) {
    const email = emails[index];
    if (email === undefined) return;
    // The words without the marks: this goes into Gmail or a doc by hand,
    // where `**bold**` would be asterisks, not bold.
    const body = marksToText(email.body);
    const text = email.subject === '' ? body : `Subject: ${email.subject}\n\n${body}`;
    void navigator.clipboard.writeText(text).then(() => {
      setCopied(TOUCHES[index] ?? null);
      setTimeout(() => setCopied(null), 1500);
    });
  }

  function createSet() {
    if (name.trim() === '') {
      setError('Give the copy set a name first.');
      return;
    }
    setError('');
    create.mutate(fieldsOf(), {
      onSuccess: onClose,
      onError: (cause: Error) => setError(salesErrorMessage(cause.message)),
    });
  }

  return (
    <Drawer
      open
      title={set === null ? 'New copy set' : 'Edit copy set'}
      onClose={() => {
        if (!pending) onClose();
      }}
      wide
      footer={
        editing ? (
          message_enabled ? (
            <>
              <Button variant="secondary" onClick={onClose} disabled={pending}>
                Close
              </Button>
              {edit?.phase === 'conflict' ? (
                <Button onClick={() => void reloadRevision()}>Reload entry</Button>
              ) : edit?.phase === 'ready' && error !== '' ? (
                <Button disabled={pending} onClick={() => persist(edit, true)}>
                  Retry save
                </Button>
              ) : (
                <Button onClick={saveEdit} disabled={pending || edit === null}>
                  {pending ? 'Saving…' : 'Save changes'}
                </Button>
              )}
            </>
          ) : undefined
        ) : (
          <>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={createSet} disabled={pending}>
              {pending ? 'Saving…' : 'Create copy set'}
            </Button>
          </>
        )
      }
    >
      {error !== '' && <InlineError>{error}</InlineError>}
      {editing && (
        <p className="drawer-autosave-note">
          {workflowPaused ? (
            <>
              Editing is paused because availability changed. Your unsaved fields stay here. Restore
              availability to continue, or close and reopen this entry to start a new edit.
            </>
          ) : message_enabled ? (
            <>
              Save changes when ready. Editing a used name or wording asks twice. Earlier DM
              versions stay intact.
            </>
          ) : (
            <>
              Changes save on their own. <SaveHint state={setSave.state} error={setSave.error} />
            </>
          )}
        </p>
      )}
      {savedNotice !== '' && <p role="status">{savedNotice}</p>}
      {message_enabled && edit !== null && (
        <CopyEditConfirmationDialog
          state={edit}
          onCancel={() => setEdit(cancelCopyEditConfirmation(edit))}
          onConfirmFirst={() => setEdit(confirmCopyEditFirst(edit))}
          onConfirmSecond={() => persist(confirmCopyEditSecond(edit, crypto.randomUUID()))}
        />
      )}
      <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} />
      <Select
        label="Type"
        disabled={message_enabled && (pending || set?.is_one_off === true)}
        value={COPY_TYPE_OPTIONS.includes(type) ? type : ''}
        options={[
          ...(COPY_TYPE_OPTIONS.includes(type) || type === ''
            ? []
            : [{ value: type, label: type }]),
          ...COPY_TYPE_OPTIONS.map((option) => ({ value: option, label: option })),
        ]}
        onChange={(e) => setType(e.target.value)}
      />
      <Select
        label="Channel"
        disabled={message_enabled && (pending || set?.is_one_off === true)}
        value={channel}
        options={[
          { value: 'email', label: 'Email' },
          { value: 'linkedin', label: 'LinkedIn' },
        ]}
        onChange={(e) => setChannel(e.target.value)}
      />
      <Input
        label="Description / angle"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />
      <p
        style={{
          margin: '0 0 var(--space-4)',
          fontSize: 'var(--font-size-xs)',
          color: 'var(--text-muted)',
        }}
      >
        Tokens fill in automatically when composing a Gmail draft. Use the buttons under each body
        to drop one in at the cursor.
      </p>
      <input
        ref={photoInput}
        type="file"
        accept="image/*"
        hidden
        aria-label="Add a photo to the set"
        onChange={(event) => pendingPhoto.current?.(event.target.files?.[0] ?? null)}
      />
      {cards.map((touch, index) => (
        <div
          key={touch}
          style={{
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--space-4)',
            marginBottom: 'var(--space-4)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 'var(--space-3)',
            }}
          >
            <strong style={{ fontSize: 'var(--font-size-sm)', color: 'var(--brand-blue)' }}>
              Email {touch}
            </strong>
            <Button variant="secondary" size="sm" onClick={() => copyToClipboard(index)}>
              {copied === touch ? 'Copied!' : 'Copy to clipboard'}
            </Button>
          </div>
          <Input
            label="Subject"
            value={emails[index]?.subject ?? ''}
            onChange={(e) => setEmail(index, 'subject', e.target.value)}
          />
          {/* Same editor as Compose and Reply, so a set is written with the
              formatting it will be sent with. The token buttons moved into
              its toolbar. */}
          <EmailBodyEditor
            label="Body"
            rows={5}
            value={emails[index]?.body ?? ''}
            onChange={(next) => setEmail(index, 'body', next)}
            tokens={COPY_TOKENS}
            onPhoto={pickPhoto}
          />
        </div>
      ))}
      {/* Nez's ask, literally: "Add another email". Eight is the ceiling, and
          at eight there is nothing left to add, so the button goes away. */}
      {(!message_enabled || !set?.is_one_off) && shownCards < TOUCHES.length && (
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setShownCards((count) => Math.min(count + 1, TOUCHES.length))}
        >
          Add another email
        </Button>
      )}
    </Drawer>
  );
}

function StudioItemDrawer({
  item,
  leads,
  onClose,
}: {
  item: ContentItem;
  leads: Lead[];
  onClose: () => void;
}) {
  const compose = useComposeDraft();
  const createSet = useCreateCopySet();
  const [error, setError] = useState('');
  const [leadId, setLeadId] = useState('');
  const [copied, setCopied] = useState(false);
  const [drafted, setDrafted] = useState(false);
  const [madeSet, setMadeSet] = useState(false);

  const sortedLeads = [...leads].sort((a, b) => leadName(a).localeCompare(leadName(b)));
  const lead = leads.find((l) => l.id === leadId) ?? null;

  function copyBody() {
    const text = item.subject === '' ? item.body : `Subject: ${item.subject}\n\n${item.body}`;
    void navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }

  /**
   * Promote approved Studio copy into the outbound library as email 1 of a
   * new set (redesign spec). The rest of the sequence is left empty: the
   * point is to start from the approved words, not to invent three more.
   */
  function useAsCopySet() {
    setError('');
    setMadeSet(false);
    createSet.mutate(
      {
        name: item.title,
        description: `From Nurture Studio: ${item.bucket}`,
        type: item.bucket,
        e1_subject: item.subject === '' ? item.title : item.subject,
        e1_body: item.body,
      },
      {
        onSuccess: () => setMadeSet(true),
        onError: (cause) => setError(salesErrorMessage(cause.message)),
      },
    );
  }

  function composeDraft() {
    if (lead === null) {
      setError('Pick a lead first.');
      return;
    }
    if (lead.email === '') {
      setError('That lead has no email address.');
      return;
    }
    setError('');
    setDrafted(false);
    compose.mutate(
      {
        to: lead.email,
        subject: replaceTokens(item.subject === '' ? item.title : item.subject, lead),
        body: replaceTokens(item.body, lead),
      },
      {
        onSuccess: () => setDrafted(true),
        onError: (cause) => setError(composeErrorMessage(cause.message)),
      },
    );
  }

  return (
    <Drawer
      open
      title={item.title}
      onClose={onClose}
      wide
      footer={
        <>
          <Button variant="secondary" onClick={copyBody}>
            {copied ? 'Copied ✓' : 'Copy text'}
          </Button>
          <Button variant="secondary" onClick={useAsCopySet} disabled={createSet.isPending}>
            {createSet.isPending ? 'Adding…' : madeSet ? 'Added ✓' : 'Use as Copy Set'}
          </Button>
          <Button onClick={composeDraft} disabled={compose.isPending}>
            {compose.isPending ? 'Creating draft…' : 'Compose to this lead'}
          </Button>
        </>
      }
    >
      {error !== '' && <InlineError>{error}</InlineError>}
      {madeSet && (
        <p
          style={{
            margin: '0 0 var(--space-4)',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-success, #1a7f37)',
          }}
        >
          Added to the Copy Library as email 1 of “{item.title}”. Open Copy Library to fill in the
          follow-ups.
        </p>
      )}
      {drafted && (
        <p
          style={{
            margin: '0 0 var(--space-4)',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-success, #1a7f37)',
          }}
        >
          Draft created. It is waiting in your Gmail Drafts.
        </p>
      )}
      <p
        style={{
          margin: '0 0 var(--space-4)',
          fontSize: 'var(--font-size-xs)',
          color: 'var(--text-muted)',
        }}
      >
        {[item.bucket, 'Approved in Nurture Studio'].filter(Boolean).join(' · ')}
      </p>
      {item.subject !== '' && (
        <p style={{ margin: '0 0 var(--space-4)', fontWeight: 'var(--font-weight-semibold)' }}>
          {item.subject}
        </p>
      )}
      <pre
        style={{
          margin: '0 0 var(--space-6)',
          padding: 'var(--space-4)',
          background: 'var(--surface-sunken)',
          borderRadius: 'var(--radius-md)',
          fontSize: 'var(--font-size-sm)',
          whiteSpace: 'pre-wrap',
          fontFamily: 'inherit',
        }}
      >
        {item.body === '' ? '(no body yet)' : item.body}
      </pre>
      <Select
        label="Compose a Gmail draft to"
        value={leadId}
        onChange={(e) => setLeadId(e.target.value)}
        options={[
          { value: '', label: 'Pick a lead…' },
          ...sortedLeads.map((candidate) => ({
            value: candidate.id,
            label: `${leadName(candidate)}${candidate.email === '' ? ' (no email)' : ''}`,
          })),
        ]}
      />
    </Drawer>
  );
}

export function CopyLibraryView({ leads }: { leads: Lead[] }) {
  const { message_enabled } = useProspectingAvailability();
  const { access } = useAuth();
  const mayPromote =
    access.status === 'approved' &&
    access.profile.email.toLowerCase() === 'kyu.renegado@4ocean.com';
  const copySetsQuery = useCopySets();
  const contentQuery = useContentItems();
  const membershipsQuery = useAllMemberships();
  const [editing, setEditing] = useState<CopySet | 'new' | null>(null);
  const [openItem, setOpenItem] = useState<ContentItem | null>(null);
  const update = useUpdateCopySet();
  const [showArchived, setShowArchived] = useState(false);
  const [archiveError, setArchiveError] = useState('');
  const sets = (copySetsQuery.data ?? []).filter((set) => showArchived || set.archived_at == null);
  const groups = message_enabled
    ? [
        {
          label: 'Copy Library',
          rows: sets.filter((set) => set.archived_at == null && set.review_state !== 'draft'),
        },
        {
          label: DRAFT_COPY_GROUP,
          rows: sets.filter((set) => set.archived_at == null && set.review_state === 'draft'),
        },
        { label: 'Archived entries', rows: sets.filter((set) => set.archived_at != null) },
      ].filter((group) => group.rows.length > 0)
    : [{ label: 'Copy Library', rows: sets }];
  function promote(set: CopySet) {
    setArchiveError('');
    update.mutate(
      { copySetId: set.id, changes: { review_state: 'library' } },
      {
        onError: (cause) => setArchiveError(salesErrorMessage(cause.message)),
      },
    );
  }
  function archive(set: CopySet) {
    setArchiveError('');
    update.mutate(
      {
        copySetId: set.id,
        changes: { archived_at: set.archived_at == null ? new Date().toISOString() : null },
      },
      {
        onError: (cause) => setArchiveError(salesErrorMessage(cause.message)),
      },
    );
  }
  const approved = (contentQuery.data ?? []).filter((item) => item.status === 'approved');
  const performance = copySetPerformance(membershipsQuery.data ?? []);
  const topSetId = topPerformingCopySetId(performance);

  if (copySetsQuery.isPending) return <Skeleton lines={3} label="Loading copy library" />;
  if (copySetsQuery.isError)
    return <InlineError>The Copy Library could not be loaded. Refresh to try again.</InlineError>;

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 'var(--space-4)' }}>
        <Button variant="secondary" size="sm" onClick={() => setEditing('new')}>
          New copy set
        </Button>
      </div>
      <Checkbox
        label="Show archived"
        checked={showArchived}
        onChange={(event) => setShowArchived(event.target.checked)}
      />
      {archiveError !== '' && <InlineError>{archiveError}</InlineError>}
      {sets.length === 0 ? (
        <EmptyState title="No copy sets yet" description="Create the first one." />
      ) : (
        groups.map((group) => (
          <Fragment key={group.label}>
            {message_enabled && <h3>{group.label}</h3>}
            <div className="copy-library-grid">
              {group.rows.map((set) => (
                <div key={set.id} className="copy-set-card">
                  <div className="copy-set-card-header">
                    <button
                      type="button"
                      className="tracker-list-link"
                      onClick={() => setEditing(set)}
                    >
                      <h3 className="copy-set-card-name">
                        {message_enabled ? copyMessageLabel(set) : set.name}
                      </h3>
                    </button>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={update.isPending}
                      aria-label={`${set.archived_at == null ? 'Archive' : 'Unarchive'} ${set.name}`}
                      onClick={() => archive(set)}
                    >
                      {set.archived_at == null ? 'Archive' : 'Unarchive'}
                    </Button>
                    <span className="copy-set-card-badges">
                      {message_enabled && set.review_state === 'draft' && (
                        <Badge tone="warning">{DRAFT_COPY_BADGE}</Badge>
                      )}
                      {set.type !== '' && <Badge tone="neutral">{set.type}</Badge>}
                      {(() => {
                        const perf = performance.get(set.id);
                        if (perf === undefined) {
                          return <Badge tone="neutral">Unused</Badge>;
                        }
                        const rate = Math.round((perf.replies / perf.leads) * 100);
                        return (
                          <Badge tone={perf.replies > 0 ? 'success' : 'neutral'}>
                            {perf.replies > 0
                              ? `${rate}% replied · ${perf.replies}/${perf.leads}`
                              : `0/${perf.leads} replied`}
                          </Badge>
                        );
                      })()}
                      {topSetId === set.id && <Badge tone="success">Top Performer 🏆</Badge>}
                    </span>
                    {message_enabled &&
                      mayPromote &&
                      set.review_state === 'draft' &&
                      set.archived_at === null && (
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={update.isPending}
                          onClick={() => promote(set)}
                        >
                          Approve for library
                        </Button>
                      )}
                  </div>
                  <div className="copy-set-emails">
                    {/* Empty quadrants are noise: a one-email set should not render
                    three "(no subject) (empty)" tiles (spec §Copy Library). */}
                    {TOUCHES.filter((touch) => {
                      const email = copyEmail(set, touch);
                      return touch === 1 || email.subject !== '' || email.body !== '';
                    }).map((touch) => {
                      const email = copyEmail(set, touch);
                      return (
                        <button
                          key={touch}
                          type="button"
                          className="copy-set-email"
                          onClick={() => setEditing(set)}
                          title={`Email ${touch}: ${email.subject}`}
                        >
                          <p className="copy-set-email-label">Email {touch}</p>
                          <p className="copy-set-email-subject">
                            {email.subject === '' ? '(no subject)' : email.subject}
                          </p>
                          <p className="copy-set-email-preview">
                            {email.body === '' ? '(empty)' : email.body.slice(0, 60)}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </Fragment>
        ))
      )}

      {/* Approved content from the Nurture Studio, same database */}
      <div style={{ marginTop: 'var(--space-8)' }}>
        <p
          style={{
            fontSize: 'var(--font-size-xs)',
            fontWeight: 'var(--font-weight-semibold)',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            color: 'var(--text-muted)',
            margin: '0 0 var(--space-4)',
          }}
        >
          From Nurture Studio ({approved.length} approved)
        </p>
        {approved.length === 0 ? (
          <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
            Nothing approved in the Nurture Studio yet.
          </p>
        ) : (
          <div className="copy-library-grid">
            {approved.map((item) => (
              <div key={item.id} className="copy-set-card">
                <div className="copy-set-card-header">
                  <button
                    type="button"
                    className="tracker-list-link"
                    onClick={() => setOpenItem(item)}
                  >
                    <h3 className="copy-set-card-name">{item.title}</h3>
                  </button>
                  {item.bucket !== '' && <Badge tone="success">{item.bucket}</Badge>}
                </div>
                <button
                  type="button"
                  className="copy-set-email"
                  onClick={() => setOpenItem(item)}
                  title={item.subject}
                >
                  <p className="copy-set-email-label">Approved</p>
                  <p className="copy-set-email-subject">
                    {item.subject === '' ? '(no subject)' : item.subject}
                  </p>
                  <p className="copy-set-email-preview">
                    {item.body === '' ? '(empty)' : item.body.slice(0, 60)}
                  </p>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {editing !== null && (
        <CopySetEditorDrawer
          set={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
      {openItem !== null && (
        <StudioItemDrawer item={openItem} leads={leads} onClose={() => setOpenItem(null)} />
      )}
    </>
  );
}

// ── Import / New lead dialogs (unchanged logic, updated class names) ───────────
